import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest, API_URL } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface AsanaInstallation {
  id: string;
  workspaceGid: string;
  workspaceName: string;
  organizationId: string;
  isActive: boolean;
  installedAt: string;
}

interface AsanaWorkspace {
  gid: string;
  name: string;
  resource_type: string;
}

interface AsanaProject {
  gid: string;
  name: string;
  resource_type: string;
}

interface AsanaSection {
  gid: string;
  name: string;
  resource_type: string;
}

interface AsanaTag {
  gid: string;
  name: string;
  color: string | null;
}

interface AsanaState {
  currentInstallation: AsanaInstallation | null;
  installations: AsanaInstallation[];
  workspaces: AsanaWorkspace[];
  projects: AsanaProject[];
  sections: AsanaSection[];
  tags: AsanaTag[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  sectionsLoading: boolean; // Separate loading state for section fetching
  tagsLoading: boolean; // Separate loading state for tag fetching
  error: string;
}

const initialState: AsanaState = {
  currentInstallation: null,
  installations: [],
  workspaces: [],
  projects: [],
  sections: [],
  tags: [],
  status: 'idle',
  sectionsLoading: false,
  tagsLoading: false,
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getAsanaInstallations = createAsyncThunk(
  'asana/getInstallations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/asana/installations?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAsanaInstallation = createAsyncThunk(
  'asana/getInstallation',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/asana/installations/${installationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectAsana = createAsyncThunk(
  'asana/disconnectAsana',
  async (installationId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/asana/installations/${installationId}`, { method: 'DELETE' });
      return installationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAsanaWorkspaces = createAsyncThunk(
  'asana/getAsanaWorkspaces',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/asana/oauth/workspaces?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAsanaProjects = createAsyncThunk(
  'asana/getAsanaProjects',
  async ({ organizationId }: { organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/asana/oauth/projects?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAsanaSections = createAsyncThunk(
  'asana/getAsanaSections',
  async ({ projectGid, organizationId }: { projectGid: string; organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/asana/oauth/projects/${projectGid}/sections?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAsanaTags = createAsyncThunk(
  'asana/getAsanaTags',
  async ({ workspaceGid, organizationId }: { workspaceGid: string; organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/asana/oauth/workspaces/${workspaceGid}/tags?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get installations
  [getAsanaInstallations.pending.type]: { status: 'loading' as const },
  [getAsanaInstallations.fulfilled.type]: (state: AsanaState, action: any) => {
    // Handle null response (404 - no installations found)
    if (!action.payload) {
      state.installations = [];
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    // Backend already filters by isActive: true
    const installations = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.installations || action.payload.installations || [];

    state.installations = installations;
    state.currentInstallation = installations[0] || null;
    state.status = 'success';
    state.error = '';
  },
  [getAsanaInstallations.rejected.type]: (state: AsanaState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installations';
  },

  // Get single installation
  [getAsanaInstallation.pending.type]: { status: 'loading' as const },
  [getAsanaInstallation.fulfilled.type]: (state: AsanaState, action: any) => {
    // Handle null response (404 - installation not found)
    if (!action.payload) {
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: {...}, metadata: {...} }
    state.currentInstallation = action.payload.response || action.payload;
    state.status = 'success';
    state.error = '';
  },
  [getAsanaInstallation.rejected.type]: (state: AsanaState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installation';
  },

  // Disconnect Asana
  [disconnectAsana.pending.type]: { status: 'loading' as const },
  [disconnectAsana.fulfilled.type]: (state: AsanaState) => {
    state.currentInstallation = null;
    state.installations = [];
    state.status = 'success';
    state.error = '';
  },
  [disconnectAsana.rejected.type]: (state: AsanaState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to disconnect Asana';
  },

  // Get Asana workspaces
  [getAsanaWorkspaces.pending.type]: { status: 'loading' as const },
  [getAsanaWorkspaces.fulfilled.type]: (state: AsanaState, action: any) => {
    if (!action.payload) {
      state.workspaces = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    const workspaces = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.workspaces || action.payload.workspaces || [];
    state.workspaces = workspaces;
    state.status = 'success';
    state.error = '';
  },
  [getAsanaWorkspaces.rejected.type]: (state: AsanaState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch Asana workspaces';
  },

  // Get Asana projects
  [getAsanaProjects.pending.type]: { status: 'loading' as const },
  [getAsanaProjects.fulfilled.type]: (state: AsanaState, action: any) => {
    if (!action.payload) {
      state.projects = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    const projects = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.projects || action.payload.projects || [];
    state.projects = projects;
    state.status = 'success';
    state.error = '';
  },
  [getAsanaProjects.rejected.type]: (state: AsanaState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch Asana projects';
  },

  // Get Asana sections - uses separate sectionsLoading to avoid full-tab reload
  [getAsanaSections.pending.type]: (state: AsanaState) => {
    state.sectionsLoading = true;
  },
  [getAsanaSections.fulfilled.type]: (state: AsanaState, action: any) => {
    state.sectionsLoading = false;
    if (!action.payload) {
      state.sections = [];
      state.error = '';
      return;
    }
    const sections = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.sections || action.payload.sections || [];
    state.sections = sections;
    state.error = '';
  },
  [getAsanaSections.rejected.type]: (state: AsanaState, action: any) => {
    state.sectionsLoading = false;
    state.error = action.payload || 'Failed to fetch Asana sections';
  },

  // Get Asana tags - uses separate tagsLoading
  [getAsanaTags.pending.type]: (state: AsanaState) => {
    state.tagsLoading = true;
  },
  [getAsanaTags.fulfilled.type]: (state: AsanaState, action: any) => {
    state.tagsLoading = false;
    if (!action.payload) {
      state.tags = [];
      state.error = '';
      return;
    }
    const tags = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.tags || action.payload.tags || [];
    state.tags = tags;
    state.error = '';
  },
  [getAsanaTags.rejected.type]: (state: AsanaState, action: any) => {
    state.tagsLoading = false;
    state.error = action.payload || 'Failed to fetch Asana tags';
  },
};

// ==============================|| SLICE ||============================== //

export const asanaSlice = createSlice({
  name: 'asana',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentInstallation(state, action) {
      state.currentInstallation = action.payload;
    },
    clearAsanaState(state) {
      state.currentInstallation = null;
      state.installations = [];
      state.workspaces = [];
      state.projects = [];
      state.sections = [];
      state.tags = [];
      state.status = 'idle';
      state.sectionsLoading = false;
      state.tagsLoading = false;
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentInstallation, clearAsanaState } = asanaSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectAsanaInstallations = (state: any) => state.asana.installations;
export const selectCurrentAsanaInstallation = (state: any) => state.asana.currentInstallation;
export const selectAsanaStatus = (state: any) => state.asana.status;
export const selectAsanaSectionsLoading = (state: any) => state.asana.sectionsLoading;
export const selectAsanaTagsLoading = (state: any) => state.asana.tagsLoading;
export const selectAsanaError = (state: any) => state.asana.error;
export const selectAsanaWorkspaces = (state: any) => state.asana.workspaces;
export const selectAsanaProjects = (state: any) => state.asana.projects;
export const selectAsanaSections = (state: any) => state.asana.sections;
export const selectAsanaTags = (state: any) => state.asana.tags;

// ==============================|| HELPER ||============================== //

export const initiateAsanaConnect = createAsyncThunk(
  'asana/initiateConnect',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      // Call API with authentication to get Asana installation URL (same as GitHub/GitLab/Jira)
      const response = await apiRequest(`/asana/install?organizationId=${organizationId}`);
      const installUrl = response.response?.installUrl || response.installUrl;

      if (!installUrl) {
        throw new Error('No installation URL returned from API');
      }

      // Redirect browser to Asana OAuth
      window.location.href = installUrl;

      return { installUrl };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export default asanaSlice.reducer;
